import React, { useState, useContext, useEffect, ReactNode } from "react";
import { Box, createStyles, makeStyles, Theme } from "@material-ui/core";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import * as Yup from "yup";
import Grid from "@material-ui/core/Grid/Grid";
import MenuItem from "@material-ui/core/MenuItem/MenuItem";
import { AxiosResponse } from "axios";
import CustomDialog, { CustomDialogProps } from "../../../components/Dialog/CustomDialog";
import CustomButton from "../../../components/Button/CustomButton";
import { ProductContext, ProductContextType } from "../../../contexts/ProductContext";
import TestRequestApi from "../../../api/services/TestRequestApi";
import CustomRadioGroup from "../../../components/Input/CustomRadioGroup";
import CustomTextField from "../../../components/Input/CustomTextField";
import CustomDatePicker from "../../../components/Input/CustomDatePicker";
import CustomDropdown from "../../../components/Input/CustomDropdown";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import TestRequestPermissionContext from "../../../contexts/TestRequestPermissionContext";
import useEnv from "../../../hooks/env";
import { SampleType, WithPredefinedChip } from "../../../components/Chip/PredefinedSampleChip";
import { parseDate } from "../../../api/services/common/FormatDateService";
import InfoBox from "../../../components/InfoBox";
import GalleryUploadButton from "../../../components/Gallery/button/GalleryUploadButton";
import GalleryButton from "../../../components/Gallery/button/GalleryButton";
import HiddenError from "../../../models/HiddenError";
import ProductSampleGallery from "./ProductSampleGallery";
import ProductAttachment from "../../../api/models/product/ProductAttachment";
import ConfirmationDialogContext from "../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import ProductSide, { PRODUCT_SIDES, PRODUCT_SIDE_DICTIONARY } from "../models/ProductSide.model";
import { ProductSample, UnknownProductSample } from "../../../api/models/product/ProductSampleReference";
import ProductSamplesApi from "../../../api/services/ProductSamplesApi";
import ProductType from "../../../api/models/product/ProductType";
import {
  containsHighlyConfidentialInfo,
  fetchHighlyConfidentialRegex,
} from "../../../functions/confidentialCheck.function";
import ConfidentialConfirmationDialog from "../../../components/Dialog/ConfidentialConfirmationDialog";

type Deliverer = TestRequestRaw.Schemas.Deliverer;

interface ProductSampleDetailsUnknownProductDialogProps extends CustomDialogProps {
  disabled?: boolean;
  testRequestID: string;
  callback: (closeDialog: boolean) => void;
}

interface ProductSampleDetailsUnknownProductForm {
  productSampleId?: string;
  unknownProductName: string;
  unknownProductDescription: string;
  deliveredBy: Deliverer;
  descriptionOrNotes: string;
  lotNumber: string;
  manufacturingDate: Date | null;
  productSampleName: string;
  receivedOn: Date | null;
  sideOfProduct: ProductSide;
  source: string;
  attachments?: TestRequestRaw.Schemas.ProductSampleAttachmentReferenceDTO[];
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      marginBottom: theme.spacing(5),
    },
  }),
);

const ProductSampleDetailsUnknownProductDialog = (props: ProductSampleDetailsUnknownProductDialogProps) => {
  const { callback, disabled, open, onClose, testRequestID } = props;
  const { api } = useEnv();
  const testRequestApi = new TestRequestApi(api);
  const productSamplesApi = new ProductSamplesApi(api);
  const { productObject, unknownProduct } = useContext(ProductContext) as ProductContextType & { productObject: any };
  const { updateProductObject } = useContext(ProductContext);
  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const { handleHttpError, showSnackbar } = useContext(ErrorSnackbarContext);
  let formRef: FormikProps<ProductSampleDetailsUnknownProductForm> | null = null;
  const classes = useStyles(props);
  const [sampleName, setSampleName] = useState("");
  const [showConfidentialDialog, setShowConfidentialDialog] = useState(false);
  const [pendingValues, setPendingValues] = useState<ProductSampleDetailsUnknownProductForm | null>(null);
  const [confidentialRegex, setConfidentialRegex] = useState<RegExp | null>(null);
  const { productSamples, newProductSample } = React.useContext(TestRequestPermissionContext);
  const isPredefined = !!productObject?.usedIn;
  const permissions = productObject ? productSamples?.[productObject.id] : newProductSample;
  const formFieldsDisabled = disabled || !permissions?.editDefinition;

  useEffect(() => {
    fetchHighlyConfidentialRegex().then(setConfidentialRegex);
  }, []);

  const yupValidationSchema = Yup.object().shape({
    company: Yup.string(),
    deliveredBy: Yup.string().required(),
    descriptionOrNotes: Yup.string(),
    id: Yup.number(),
    lotNumber: Yup.string(),
    manufacturingDate: Yup.string().nullable(),
    productSampleName: Yup.string()
      .required("Please enter a sample name")
      .test(
        "invalid character test",
        "Name cannot contain a hash '#' symbol",
        (value) => value && !value.includes("#"),
      ),
    productName: Yup.string(),
    productVariant: Yup.string(),
    receivedOn: Yup.string().nullable(),
    sideOfProduct: Yup.string().required(),
    source: Yup.string(),
  });

  useEffect(() => {
    setSampleName(productObject?.productSampleName || unknownProduct?.name || "");
  }, [productObject, unknownProduct]);

  const showGallery = open && (productObject || productObject?.id === 0) && testRequestID && productObject?.attachments;
  const showExtraSaveButton = !disabled && !showGallery && !productObject?.id && permissions?.editAttachments;
  const showGalleryAddButton = !disabled && showGallery && permissions?.editAttachments;

  const save = (values: ProductSampleDetailsUnknownProductForm): Promise<ProductSample> => {
    const payload: UnknownProductSample = {
      attachments: values.attachments as TestRequestRaw.Schemas.ProductSampleAttachmentReferenceDTO[],
      id: productObject?.id as number,
      productSampleName: values.productSampleName,
      unknownProductId: unknownProduct?.id as number,
      unknownProductName: unknownProduct?.name as string,
      unknownProductDescription: unknownProduct?.description as string,
      deliveredBy: values.deliveredBy,
      descriptionOrNotes: values.descriptionOrNotes,
      lotNumber: values.lotNumber,
      manufacturingDate: values.manufacturingDate?.toISOString() as string,
      receivedOn: values.receivedOn?.toISOString() as string,
      sideOfProduct: values.sideOfProduct,
      source: values.source,
      type: "UNKNOWN_PRODUCT" as ProductType,
    };

    if (productObject) {
      return productSamplesApi.updateProductSample(testRequestID, payload as UnknownProductSample).then((res) => {
        showSnackbar("Product sample updated", "success");
        return res.data;
      });
    }

    return testRequestApi
      .createUnknownProduct({
        ...payload,
        sampleName: payload.productSampleName,
      })
      .then((res: AxiosResponse) => {
        if (permissions?.editDefinition && testRequestID) {
          const newProductSampleId = Number(res.data.id);
          return productSamplesApi.updateTestRequestProducts(testRequestID, [newProductSampleId]).then(() => {
            showSnackbar("Product sample created", "success");
            onClose();
            return {
              ...res.data,
              attachments: [],
            };
          });
        }
        return res.data;
      });
  };

  const update = (
    values: ProductSampleDetailsUnknownProductForm,
    actions: FormikHelpers<ProductSampleDetailsUnknownProductForm>,
  ): void => {
    if (disabled || !formRef?.dirty || !permissions?.editDefinition) {
      actions.resetForm();
      callback(true);
      onClose();
    } else {
      const fieldsToCheck = [
        values.productSampleName,
        values.source,
        values.descriptionOrNotes,
        values.unknownProductName,
        values.unknownProductDescription,
        values.lotNumber,
      ];

      if (confidentialRegex && containsHighlyConfidentialInfo(fieldsToCheck, confidentialRegex)) {
        setPendingValues(values);
        setShowConfidentialDialog(true);
        return;
      }

      save(values)
        .then(() => {
          actions.resetForm();
          callback(true);
          onClose();
        })
        .catch(handleHttpError);
    }
  };

  return (
    <>
      <ProductContext.Consumer>
        {() => {
          return (
            <CustomDialog fullWidth maxWidth="lg" onClose={onClose} open={open}>
              <h2 className={classes.header}>
                {isPredefined ? (
                  <WithPredefinedChip
                    details={{
                      testRequestId: productObject.usedIn,
                      sampleType: SampleType.Product,
                    }}
                    spacing={2}
                    text={`UNKNOWN PRODUCT ${sampleName}`}
                  />
                ) : (
                  <>UNKNOWN PRODUCT {sampleName}</>
                )}
              </h2>
              <h3 className={classes.header}>Product Sample Definition</h3>
              <Formik
                innerRef={(ref) => {
                  if (ref) {
                    formRef = ref;
                  }
                }}
                initialValues={
                  {
                    productSampleId: productObject?.id?.toString(),
                    unknownProductName: unknownProduct?.name || "",
                    unknownProductDescription: unknownProduct?.description || "",
                    deliveredBy: productObject?.deliveredBy || "",
                    descriptionOrNotes: productObject?.descriptionOrNotes || "",
                    lotNumber: productObject?.lotNumber || "",
                    manufacturingDate: parseDate(productObject?.manufacturingDate),
                    productSampleName: sampleName,
                    receivedOn: parseDate(productObject?.receivedOn),
                    sideOfProduct: productObject?.sideOfProduct || "OPEN",
                    source: productObject?.source || "",
                  } as ProductSampleDetailsUnknownProductForm
                }
                onSubmit={update}
                validationSchema={yupValidationSchema}
              >
                {(formikProps: FormikProps<ProductSampleDetailsUnknownProductForm>): ReactNode => {
                  const { errors, isSubmitting, handleBlur, handleChange, setFieldValue, touched, values } =
                    formikProps;
                  return (
                    <Form>
                      <Box pb={4}>
                        <Grid container justify="flex-start" direction="row" spacing={4}>
                          <Grid item lg={6} md={6} sm={6} xs={6}>
                            <InfoBox text="Keep the sample name as short as possible.">
                              <CustomTextField
                                autoFocus
                                fullWidth
                                mandatory
                                disabled={formFieldsDisabled}
                                name="productSampleName"
                                id="productSampleName"
                                label="Sample Name"
                                value={values.productSampleName}
                                helperText={[
                                  errors.productSampleName && touched.productSampleName ? errors.productSampleName : "",
                                ].join(" ")}
                                error={!!(errors.productSampleName && touched.productSampleName)}
                                onChange={(e) => {
                                  setSampleName(e.target.value);
                                  handleChange(e);
                                }}
                                onBlur={handleBlur}
                              />
                            </InfoBox>
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <CustomTextField
                              fullWidth
                              disabled
                              name="unknownProductName"
                              id="unknownProductName"
                              label="Unknown Product Name"
                              value={values.unknownProductName}
                            />
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            {productObject && (
                              <CustomTextField
                                fullWidth
                                disabled
                                name="productSampleId"
                                id="productSampleId"
                                label="Product Sample ID"
                                value={values.productSampleId}
                              />
                            )}
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <CustomDatePicker
                              disabled={formFieldsDisabled}
                              label="Received On"
                              value={values.receivedOn}
                              onChange={(date) => setFieldValue("receivedOn", date)}
                            />
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <InfoBox text="This can be the Customer, Requester or Plant.">
                              <CustomTextField
                                fullWidth
                                disabled={formFieldsDisabled}
                                name="source"
                                id="source"
                                label="Source"
                                value={values.source}
                                type="source"
                                helperText={[errors.source && touched.source ? errors.source : ""].join(" ")}
                                error={!!(errors.source && touched.source)}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            </InfoBox>
                          </Grid>
                          <Grid item lg={6} md={6} sm={6} xs={6}>
                            <CustomTextField
                              fullWidth
                              disabled
                              name="unknownProductDescription"
                              id="unknownProductDescription"
                              label="Product Description"
                              value={values.unknownProductDescription}
                              type="unknownProductDescription"
                            />
                          </Grid>
                          <Grid item xs={3}>
                            <InfoBox text="Indicate the side of the tape you want to test. If you want to test both sides of a tape, please create two product samples. One per side!">
                              <CustomDropdown
                                mandatory
                                disabled={formFieldsDisabled}
                                labelText="Side of the Product"
                                name="sideOfProduct"
                                id="sideOfProduct"
                                value={values.sideOfProduct}
                                type="sideOfProduct"
                                error={!!(errors.sideOfProduct && touched.sideOfProduct)}
                                onChange={handleChange}
                              >
                                {PRODUCT_SIDES.map((ps) => (
                                  <MenuItem value={ps}>{PRODUCT_SIDE_DICTIONARY[ps] || ""}</MenuItem>
                                ))}
                              </CustomDropdown>
                            </InfoBox>
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <CustomRadioGroup
                              disabled={formFieldsDisabled}
                              error={!!(errors.deliveredBy && touched.deliveredBy)}
                              formLabel="Delivered By"
                              mandatory
                              name="deliveredBy"
                              options={["CSC Lab", "Requester"]}
                              onChange={handleChange}
                              value={values.deliveredBy}
                            />
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <CustomDatePicker
                              disabled={formFieldsDisabled}
                              helperText="When the roll was produced"
                              label="Manufacturing Date"
                              value={values.manufacturingDate}
                              onChange={(date) => setFieldValue("manufacturingDate", date)}
                            />
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <CustomTextField
                              fullWidth
                              disabled={formFieldsDisabled}
                              name="lotNumber"
                              id="lotNumber"
                              label="Lot Number"
                              value={values.lotNumber}
                              type="lotNumber"
                              helperText={[errors.lotNumber && touched.lotNumber ? errors.lotNumber : ""].join(" ")}
                              error={!!(errors.lotNumber && touched.lotNumber)}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <CustomTextField
                              fullWidth
                              disabled={formFieldsDisabled}
                              name="descriptionOrNotes"
                              id="descriptionOrNotes"
                              label="Description or Notes"
                              value={values.descriptionOrNotes}
                              type="descriptionOrNotes"
                              helperText={[
                                errors.descriptionOrNotes && touched.descriptionOrNotes
                                  ? errors.descriptionOrNotes
                                  : "",
                              ].join(" ")}
                              error={!!(errors.descriptionOrNotes && touched.descriptionOrNotes)}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                      <Box display="flex" flexDirection="row">
                        {showGalleryAddButton && (
                          <Box marginRight={2}>
                            <GalleryUploadButton hideIfNonEmpty label="Add Attachment" />
                          </Box>
                        )}
                        {showExtraSaveButton && (
                          <GalleryButton
                            icon="ADD"
                            text="Add Attachment"
                            onClick={() =>
                              showConfirmationDialog({
                                headline: "Save Product Sample",
                                text: "You have to save the Product Sample to add attachments.",
                                confirmText: "Save",
                              })
                                .then(() => {
                                  return formRef?.validateForm();
                                })
                                .then((formErrors) => {
                                  if (formErrors && Object.keys(formErrors).length > 0) {
                                    formRef?.submitForm();
                                    throw new HiddenError("form validation failed");
                                  }
                                  return formRef?.values;
                                })
                                .then((formValues) => {
                                  if (formValues) {
                                    return save(formValues).then((response) => {
                                      callback(false);
                                      formRef?.setFieldValue("productSampleId", response.id || null);
                                      updateProductObject(response);
                                    });
                                  }
                                })
                                .catch(handleHttpError)
                            }
                          />
                        )}
                      </Box>
                      {showGallery && (
                        <ProductSampleGallery
                          editable={(!disabled && permissions?.editAttachments) || false}
                          testRequestId={testRequestID}
                          productSampleId={productObject?.id}
                          productSampleName={sampleName}
                          productSampleAttachments={productObject?.attachments as ProductAttachment[]}
                          onChange={(attachments) => {
                            const existingAttachments = productObject?.attachments;
                            if (existingAttachments) {
                              existingAttachments.splice(0, existingAttachments.length, ...attachments);
                            }
                          }}
                        />
                      )}
                      <Grid container direction="row" justify="flex-end">
                        <CustomButton
                          disabled={isSubmitting}
                          primary
                          text={formFieldsDisabled ? "Close" : "Save and Close"}
                          type="submit"
                        />
                      </Grid>
                    </Form>
                  );
                }}
              </Formik>
            </CustomDialog>
          );
        }}
      </ProductContext.Consumer>
      {/* Confidential confirmation dialog */}
      {showConfidentialDialog && (
        <ConfidentialConfirmationDialog
          objectName="unknown product sample"
          open={showConfidentialDialog}
          onCancel={() => {
            setShowConfidentialDialog(false);
            setPendingValues(null);
          }}
          onProceed={() => {
            if (pendingValues && testRequestID) {
              testRequestApi
                .updateConfidentiality(testRequestID, "HIGHLY_CONFIDENTIAL")
                .then(() => {
                  showSnackbar("Test request set to highly confidential", "success");
                  return save(pendingValues);
                })
                .then(() => {
                  formRef?.resetForm();
                  callback(true);
                  onClose();
                  showSnackbar("Unknown product sample updated", "success");
                })
                .catch(handleHttpError);
            }
            setShowConfidentialDialog(false);
            setPendingValues(null);
          }}
        />
      )}
    </>
  );
};

export default ProductSampleDetailsUnknownProductDialog;
